import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

/**
 * A searchable contact picker, with an inline "+ New contact".
 *
 * ─── WHY ────────────────────────────────────────────────────────────────────
 * The opportunity form's Contact field was a native <select> carrying EVERY
 * contact — 7,961 options in production, in `updated_at DESC` order, with no
 * search. Daniel, 22 Oct: "so long it is impossible to use". A native select
 * has no type-ahead beyond first-letter jumping, so finding a known contact
 * meant scrolling thousands of rows in an arbitrary order.
 *
 * ─── ⚠ THE SEARCH IS CLIENT-SIDE, AND THAT IS A MEASURED CHOICE ─────────────
 * crm_contacts.first_name / last_name / email / phone are NOT encrypted — they
 * are EXPLICITLY ALLOW-LISTED plaintext in database/plaintextColumnAllowlist.js
 * (a recorded decision, and six writers insert them in the clear). So there is
 * no ciphertext to defeat here and no reason to decrypt anything: the rows are
 * ALREADY in memory, because the <select> this replaces was rendering all of
 * them. Filtering that array is the smallest possible change and costs no
 * round trip.
 *
 * ⚠⚠ DO NOT confuse these with crm_opportunities.contact_name / contact_email /
 * contact_phone / contact_city / contact_state, which P105h (#666) DID encrypt
 * and which need `matchesNameTokens`, the in-app twin in
 * database/nameSearchSql.js. Those are the DEAL's copy of the details. This
 * picker searches the CONTACTS table, which is a different question.
 *
 * ⚠ KNOWN BOUND, STATED RATHER THAN HIDDEN: the array comes from
 * `crm:contacts:list`, whose default cap is 10,000 (routes/crm.js:1081). At
 * 7,961 contacts there is headroom, but past the cap this picker would search
 * only what was loaded. It is never WORSE than the <select> it replaces — that
 * rendered exactly the same array — and `atLoadCap` surfaces the condition
 * instead of letting it pass silently.
 *
 * ⚠ NO VALUE IS LOGGED OR RENDERED BEYOND WHAT STAFF ALREADY SEE on the
 * Contacts tab. Email and phone appear as a MASKED hint, enough to tell two
 * people with the same name apart without putting a full address on screen.
 */

const MIN_QUERY = 2;
const MAX_RESULTS = 20;
export const DEBOUNCE_MS = 150;
/** The cap crm:contacts:list applies when the caller passes no explicit limit. */
export const CONTACT_LOAD_CAP = 10000;

/**
 * ⛔⛔ NEVER PAINT AN ENVELOPE. Found by this component's own test: the masks
 * covered email and phone, but the NAME was rendered raw, so a row carrying
 * `enc:gcm:…` would have appeared on screen as if it were a person's name.
 * These columns are plaintext by decision today — but a future card could
 * encrypt them, and a picker that renders ciphertext as a label is both
 * useless and a disclosure of the ciphertext itself.
 */
export const ENVELOPE_RE = /^enc:(gcm|v1):/;
export function safeText(v) {
  const s = String(v == null ? '' : v);
  return ENVELOPE_RE.test(s) ? '' : s;
}

/** "ab…@domain", "…1234" — enough to disambiguate, not enough to be a leak. */
export function maskEmail(email) {
  const s = safeText(email).trim();
  if (!s || !s.includes('@')) return '';
  const [local, domain] = s.split('@');
  // ⚠ THREE characters, not two. With two, "js@" and "jsr@" both rendered
  // "js…@" — identical hints for two different people, which defeats the only
  // reason the hint exists. Found by the disambiguation test.
  const head = local.slice(0, 3);
  return `${head}${local.length > 3 ? '…' : ''}@${domain}`;
}
export function maskPhone(phone) {
  const digits = safeText(phone).replace(/\D/g, '');
  if (digits.length < 4) return '';
  return `…${digits.slice(-4)}`;
}

/**
 * Rank and filter. Pure, so the tests drive it directly.
 *
 * ⚠ EVERY TOKEN MUST MATCH, IN ANY ORDER — the same rule the SQL name search
 * uses (database/nameSearchSql.js buildNameSearchClause). "smith john" and
 * "john smith" must both find the same person, because no single column holds
 * a first AND last name and staff type them in either order.
 */
export function searchContacts(contacts, query, limit = MAX_RESULTS) {
  const tokens = String(query || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const out = [];
  for (const c of contacts || []) {
    const first = String(c.first_name || '').toLowerCase();
    const last = String(c.last_name || '').toLowerCase();
    const org = String(c.organization || '').toLowerCase();
    const email = String(c.email || '').toLowerCase();
    const hay = `${first} ${last} ${org} ${email}`;
    if (!tokens.every((t) => hay.includes(t))) continue;
    // A name that STARTS with the first token sorts above an incidental hit,
    // so typing "jo" offers the Johns before the Mejorados.
    const starts = first.startsWith(tokens[0]) || last.startsWith(tokens[0]);
    out.push({ c, rank: starts ? 0 : 1, sortKey: `${last} ${first}` });
    // ⛔ No early break: ranking needs the whole set, and the slice happens
    // after the sort. Stopping at `limit` matches would show whichever rows
    // happened to come first in the array, which is the problem we are fixing.
  }
  out.sort((a, b) => (a.rank - b.rank) || a.sortKey.localeCompare(b.sortKey));
  return out.slice(0, limit).map((x) => x.c);
}

export default function ContactPicker({
  contacts = [],
  value = '',
  onChange,
  onCreateContact,
  inputStyle = {},
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [adding, setAdding] = useState(false);
  const boxRef = useRef(null);

  const selected = useMemo(
    () => (contacts || []).find((c) => String(c.id) === String(value)) || null,
    [contacts, value],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const results = useMemo(
    () => (debounced.trim().length >= MIN_QUERY ? searchContacts(contacts, debounced) : []),
    [contacts, debounced],
  );

  useEffect(() => { setCursor(0); }, [debounced]);

  // Close on an outside click, so the list never strands over the form.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = useCallback((c) => {
    onChange(c ? String(c.id) : '');
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[cursor]) { e.preventDefault(); pick(results[cursor]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const atLoadCap = (contacts || []).length >= CONTACT_LOAD_CAP;
  const label = (c) => `${safeText(c.first_name)} ${safeText(c.last_name)}`.trim() || '(no name)';
  const hint = (c) => [maskEmail(c.email), maskPhone(c.phone), safeText(c.organization)]
    .filter(Boolean).join(' · ');

  return (
    <div ref={boxRef} style={{ position: 'relative' }} data-testid="contact-picker">
      {selected && !open ? (
        <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span>
            {label(selected)}
            {hint(selected) && <span style={{ color: '#6C757D', fontSize: 11 }}> — {hint(selected)}</span>}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button type="button" disabled={disabled} onClick={() => { setOpen(true); setQuery(''); }}
              style={linkBtn} aria-label="Change contact">Change</button>
            <button type="button" disabled={disabled} onClick={() => pick(null)}
              style={linkBtn} aria-label="Clear contact">Clear</button>
          </span>
        </div>
      ) : (
        <input
          type="text"
          value={query}
          disabled={disabled}
          placeholder={`Type ${MIN_QUERY}+ letters of a name…`}
          aria-label="Search contacts"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={inputStyle}
        />
      )}

      {open && !adding && (
        <div style={dropdown}>
          {debounced.trim().length < MIN_QUERY ? (
            <div style={note}>Type at least {MIN_QUERY} letters.</div>
          ) : results.length === 0 ? (
            <div style={note}>No match.</div>
          ) : results.map((c, i) => (
            <div
              key={c.id}
              role="option"
              aria-selected={i === cursor}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              style={{ ...row, background: i === cursor ? '#E8F4F6' : 'transparent' }}
            >
              <div style={{ fontWeight: 500 }}>{label(c)}</div>
              {hint(c) && <div style={{ fontSize: 11, color: '#6C757D' }}>{hint(c)}</div>}
            </div>
          ))}
          <div style={{ borderTop: '1px solid #DEE2E6' }}>
            <div onMouseDown={(e) => { e.preventDefault(); setAdding(true); }}
              style={{ ...row, color: '#2E8B98', fontWeight: 500 }} role="button">
              + New contact
            </div>
            <div onMouseDown={(e) => { e.preventDefault(); pick(null); }} style={{ ...row, color: '#6C757D' }} role="button">
              None
            </div>
          </div>
          {atLoadCap && (
            <div style={{ ...note, color: '#B8860B' }}>
              ⚠ {contacts.length} contacts loaded (the list cap). Search covers what is loaded.
            </div>
          )}
        </div>
      )}

      {adding && (
        <InlineNewContact
          onCancel={() => setAdding(false)}
          onCreate={async (fields) => {
            const created = await onCreateContact(fields);
            setAdding(false);
            if (created && created.id) { onChange(String(created.id)); setOpen(false); setQuery(''); }
            return created;
          }}
        />
      )}
    </div>
  );
}

/**
 * The inline create form.
 *
 * ⛔⛔ IT DOES NOT WRITE. It hands the fields to `onCreateContact`, which the
 * parent wires to the SAME channel Contacts → Add Contact uses
 * (`crm:contacts:create`, routes/crm.js:1502). That is the whole point: one
 * writer means one set of validation, one audit row, one set of GHL rules and
 * one encryption path. A second INSERT here would be a second policy.
 */
export function InlineNewContact({ onCancel, onCreate }) {
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phone: '', referralAnswer: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const partnerChosen = f.referralAnswer === 'partner';

  const submit = async () => {
    setErr(null);
    if (!f.firstName.trim() || !f.lastName.trim()) { setErr('First and last name are required.'); return; }
    // ⛔⛔ THE REFERRAL QUESTION IS NOT OPTIONAL AND IS NOT DEFAULTED HERE.
    // Daniel's ruling, 2026-08-01 (Card 27): it is answered AT CREATION,
    // "not optional, not skippable", and migration 161 has a BEFORE INSERT
    // trigger as the backstop. routes/crm.js:1520 refuses anything that is
    // neither "partner" nor "none" with REFERRAL_ANSWER_REQUIRED.
    //
    // ⚠⚠ SO A FOUR-FIELD INLINE FORM COULD NOT CREATE A CONTACT AT ALL — every
    // submit would be refused. And quietly sending referralAnswer:"none" to
    // make it work would answer a mandatory question on the user's behalf,
    // which is the ruling defeated rather than honoured. It is asked.
    if (f.referralAnswer !== 'none') {
      setErr(partnerChosen
        ? 'Partner referrals need the full form — use Contacts → Add Contact.'
        : 'Answer the referral question before saving.');
      return;
    }
    setBusy(true);
    try {
      const r = await onCreate({
        firstName: f.firstName.trim(), lastName: f.lastName.trim(),
        email: f.email.trim() || null, phone: f.phone.trim() || null,
        referralAnswer: 'none',
      });
      // ⚠ The handler answers { error: true, message } on refusal — not a
      // thrown exception and not { success:false }. Matching the shape
      // CRMPage's Add Contact already reads (CRMPage.js:136).
      if (r && r.error) setErr(r.message || 'Could not create the contact.');
    } catch (e) {
      setErr(e && e.message ? e.message : 'Could not create the contact.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...dropdown, padding: 10 }} data-testid="inline-new-contact">
      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>New contact</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <input aria-label="First name" placeholder="First name" value={f.firstName}
          onChange={(e) => set('firstName', e.target.value)} style={miniInput} />
        <input aria-label="Last name" placeholder="Last name" value={f.lastName}
          onChange={(e) => set('lastName', e.target.value)} style={miniInput} />
        <input aria-label="Email" placeholder="Email" value={f.email}
          onChange={(e) => set('email', e.target.value)} style={miniInput} />
        <input aria-label="Phone" placeholder="Phone" value={f.phone}
          onChange={(e) => set('phone', e.target.value)} style={miniInput} />
      </div>
      <div style={{ marginTop: 6 }}>
        <label style={{ fontSize: 11, color: '#6C757D', display: 'block', marginBottom: 2 }}>
          Referred by a partner? (required)
        </label>
        <select aria-label="Referral answer" value={f.referralAnswer}
          onChange={(e) => set('referralAnswer', e.target.value)} style={miniInput}>
          <option value="">Choose…</option>
          <option value="none">No — direct</option>
          <option value="partner">Yes — a partner</option>
        </select>
        {partnerChosen && (
          <div style={{ fontSize: 11, color: '#B8860B', marginTop: 4 }}>
            Partner referrals need the partner chosen too — use Contacts → Add Contact.
          </div>
        )}
      </div>
      {err && <div style={{ color: '#C0392B', fontSize: 11, marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={linkBtn}>Cancel</button>
        <button type="button" onClick={submit} disabled={busy || partnerChosen}
          style={{ ...linkBtn, color: partnerChosen ? '#ADB5BD' : '#2E8B98', fontWeight: 600 }}>
          {busy ? 'Saving…' : 'Save & select'}
        </button>
      </div>
    </div>
  );
}

const dropdown = {
  position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, marginTop: 2,
  background: '#FFF', border: '1px solid #DEE2E6', borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto',
};
const row = { padding: '6px 10px', cursor: 'pointer', fontSize: 13 };
const note = { padding: '8px 10px', fontSize: 12, color: '#6C757D' };
const linkBtn = {
  background: 'none', border: 'none', color: '#6C757D', cursor: 'pointer',
  fontSize: 11, padding: 0, textDecoration: 'underline',
};
const miniInput = {
  padding: '6px 8px', border: '1px solid #CED4DA', borderRadius: 4, fontSize: 12, width: '100%',
};
